/**
 * Caja.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "Cuanta plata tengo, donde esta, y que paso hoy."
 *
 * Y la contesta sin mentir en la unica parte donde es facil mentir: la caja
 * no es una, son varias cuentas, y cada una tiene SU moneda. No hay un
 * saldo; hay un saldo en pesos y un saldo en dolares, y son numeros distintos
 * que no se suman. El consolidado existe, pero siempre con la cotizacion a la
 * vista, porque es una conversion y no un total.
 *
 * Todo lo que escribe (movimiento, transferencia, cierre) pasa por un paso de
 * revision. Un asiento de caja no tiene Ctrl+Z: lo cargado a mano se anula con
 * su espejo (0155), y lo demas se deshace desde la operacion que lo genero.
 *
 * (0155) Abre en HOY, no en el mes: quien cierra a la noche quiere ver su dia.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeftRight, ClipboardCheck, MoreHorizontal, Plus, Repeat, Wallet } from "lucide-react";
import { formatear } from "@tora/shared";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Segmented } from "@/components/ui/segmented";
import { PieDeLista } from "@/components/ui/pie-de-lista";
import { DialogArqueo, type ResultadoArqueo } from "@/components/caja/DialogArqueo";
import { DialogAnularMovimiento } from "@/components/caja/DialogAnularMovimiento";
import { DialogRevisar } from "@/components/caja/DialogRevisar";
import { Aviso } from "@/components/ui/aviso";
import { DialogMovimiento } from "@/components/caja/DialogMovimiento";
import { DialogTransferencia } from "@/components/caja/DialogTransferencia";
import { FranjaSaldos, chequesEnCartera } from "@/components/caja/FranjaSaldos";
import { Movimientos, type FiltrosVisibles } from "@/components/caja/Movimientos";
import { textoCotizacion } from "@/components/caja/logica";
import {
  useAnularMovimiento,
  useCajas,
  useCierreCaja,
  useFrecuentesCaja,
  useParaRevisar,
  useRevisarMovimiento,
  useCheques,
  useConfig,
  useCuentasFinancieras,
  useMovimientosCaja,
  useProveedores,
  useRegistrarMovimiento,
  useSocios,
  useTarjetas,
  useTotalesCaja,
  useTransferencia,
} from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import {
  atajosPeriodo,
  hoyComoRango,
  mismoPeriodo,
  rangoDelPeriodo,
  type Periodo,
} from "@/lib/periodo";
import { useVerMas } from "@/lib/usar-ver-mas";
import {
  aNumero,
  type CuentaFinanciera,
  type FiltrosMovimientosCaja,
  type MovimientoCaja,
} from "@/lib/tipos";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/caja")({ component: Caja });

/**
 * Los filtros se recuerdan mientras dure la sesión: salir de Caja y volver
 * los devolvía al período de arranque, y quien estaba mirando el 04/11 tenía que
 * buscarlo de nuevo. La búsqueda de texto no se guarda: es de un momento.
 */
const CLAVE_FILTROS = "caja:filtros";
const CLAVE_VISTA = "caja:vista";

function filtrosGuardados(): FiltrosVisibles {
  const base: FiltrosVisibles = { texto: "", cuentaId: "", tipo: "", periodo: hoyComoRango() };
  try {
    const crudo = sessionStorage.getItem(CLAVE_FILTROS);
    if (!crudo) return base;
    const g = JSON.parse(crudo) as {
      cuentaId?: string;
      tipo?: string;
      periodo?: Periodo;
      atajo?: string;
    };
    // Un atajo («Hoy», «Esta semana», «Este mes») se vuelve a resolver contra
    // el reloj: guardado como fechas, «Hoy» elegido ayer seguía mostrando ayer.
    const atajo = g.atajo ? atajosPeriodo().find((a) => a.id === g.atajo) : undefined;
    const periodo =
      atajo?.periodo ??
      (g.periodo && typeof g.periodo === "object" && "tipo" in g.periodo
        ? g.periodo
        : base.periodo);
    return {
      ...base,
      cuentaId: typeof g.cuentaId === "string" ? g.cuentaId : "",
      tipo: typeof g.tipo === "string" ? g.tipo : "",
      periodo,
    };
  } catch {
    return base;
  }
}

type Vista = "saldos" | "movimientos";

function vistaGuardada(): Vista {
  try {
    return localStorage.getItem(CLAVE_VISTA) === "saldos" ? "saldos" : "movimientos";
  } catch {
    return "movimientos";
  }
}

function Caja(): JSX.Element {
  const [filtros, setFiltros] = useState<FiltrosVisibles>(filtrosGuardados);
  useEffect(() => {
    try {
      const { cuentaId, tipo, periodo } = filtros;
      const atajo = atajosPeriodo().find((a) => mismoPeriodo(a.periodo, periodo))?.id;
      sessionStorage.setItem(CLAVE_FILTROS, JSON.stringify({ cuentaId, tipo, periodo, atajo }));
    } catch {
      // Sin almacenamiento (modo privado): se pierde al salir, como antes.
    }
  }, [filtros]);

  /*
    En el celular, Saldos y Movimientos van en pestañas: los movimientos
    estaban debajo de una pantalla y media de saldos y el primero aparecía a
    931 px. En la compu se ven los dos juntos, como siempre. Se recuerda la
    última pestaña elegida.
  */
  const [vista, setVista] = useState<Vista>(vistaGuardada);
  const elegirVista = (v: Vista): void => {
    setVista(v);
    try {
      localStorage.setItem(CLAVE_VISTA, v);
    } catch {
      // idem
    }
  };
  const [abrirMovimiento, setAbrirMovimiento] = useState(false);
  // Dos botones y no uno: mover plata entre cajas de la misma moneda y comprar
  // o vender dolares son operaciones distintas. Una no genera resultado y la
  // otra si.
  const [transferencia, setTransferencia] = useState<"cerrado" | "mover" | "cambiar">("cerrado");
  const [abrirArqueo, setAbrirArqueo] = useState(false);
  // El resultado se guarda aparte del dialogo: el conteo es a ciegas, asi que
  // el esperado y la diferencia recien existen despues de confirmar.
  const [arqueo, setArqueo] = useState<ResultadoArqueo[] | null>(null);
  const [aAnular, setAAnular] = useState<MovimientoCaja | null>(null);
  // (0156) La lista de lo que falta revisar. La campanita trae `?revisar=1`.
  const [abrirRevisar, setAbrirRevisar] = useState(
    () =>
      typeof window !== "undefined" && new URLSearchParams(window.location.search).has("revisar"),
  );

  const { data: cajas } = useCajas();
  const { data: totales } = useTotalesCaja();
  const { data: cuentas } = useCuentasFinancieras();
  const { data: config } = useConfig();
  // Sin periodo a proposito: lo que falta acreditar es lo que falta, mire el
  // mes que mire.
  const { data: tarjetas } = useTarjetas({ acreditado: false });
  // Los cheques cobrados que siguen en el cajon: ya estan en el saldo de su
  // cuenta, y la franja lo dice para que el Banco no se lea como el extracto.
  const { data: cheques } = useCheques("EN_CARTERA");
  // Para que un retiro o un aporte diga de que socio es.
  const { data: socios } = useSocios();

  // La ventana de filas, que crece con «Ver más» y vuelve a la primera pagina
  // cada vez que cambia un filtro.
  const { limite, verMas, reiniciar } = useVerMas(200);
  useEffect(() => reiniciar(), [filtros, reiniciar]);

  // La busqueda va a la base y no al array ya cargado: filtrar en el cliente
  // encuentra solo dentro de los primeros 200, y el pie sumaria otra cosa.
  const busqueda = filtros.texto.trim();
  const filtrosServidor = useMemo<FiltrosMovimientosCaja>(
    () => ({
      limite,
      ...rangoDelPeriodo(filtros.periodo),
      ...(busqueda ? { q: busqueda } : {}),
      ...(filtros.cuentaId ? { cuenta_id: Number(filtros.cuentaId) } : {}),
      ...(filtros.tipo ? { tipo: filtros.tipo as "INGRESO" | "EGRESO" } : {}),
    }),
    [limite, busqueda, filtros.cuentaId, filtros.tipo, filtros.periodo],
  );
  const { data: movimientos, isFetching } = useMovimientosCaja(filtrosServidor);

  const listaCuentas = useMemo(() => cuentas?.filas ?? [], [cuentas]);
  const cotizacionSistema = aNumero(config?.cotizacion_vigente) || aNumero(totales?.cotizacion);

  const registrar = useRegistrarMovimiento();
  const { data: proveedores } = useProveedores();
  const transferir = useTransferencia();
  const cerrar = useCierreCaja();
  const anular = useAnularMovimiento();
  const { data: frecuentes } = useFrecuentesCaja();
  const { data: paraRevisar } = useParaRevisar();
  const revisar = useRevisarMovimiento();
  const cuantosRevisar = paraRevisar?.filas.length ?? 0;

  return (
    /*
      `alto="fijo"`: la pagina no scrollea, scrollea la lista. Sin esto, en un
      notebook la franja de saldos empuja la tabla abajo del pliegue y el pie
      con los totales —que es sticky— nunca se ve.
    */
    <Pagina alto="fijo">
      <PageHeader
        titulo="Caja"
        icono={Wallet}
        acciones={
          <>
            {/* En el celular: «Movimiento» a la vista y lo demás en «Más». Los
                cuatro botones ocupaban tres renglones arriba de todo. */}
            <div className="flex items-center gap-2 md:hidden">
              <Button type="button" onClick={() => setAbrirMovimiento(true)}>
                <Plus aria-hidden="true" />
                Movimiento
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" aria-label="Más acciones de caja">
                    <MoreHorizontal aria-hidden="true" />
                    Más
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setTransferencia("mover")}>
                    <ArrowLeftRight aria-hidden="true" />
                    Transferir
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setTransferencia("cambiar")}>
                    <Repeat aria-hidden="true" />
                    Comprar o vender dólares
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      setArqueo(null);
                      setAbrirArqueo(true);
                    }}
                  >
                    <ClipboardCheck aria-hidden="true" />
                    Cerrar la caja
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="hidden md:contents">
              <Button type="button" variant="outline" onClick={() => setTransferencia("mover")}>
                <ArrowLeftRight aria-hidden="true" />
                Transferir
              </Button>
              <Button type="button" variant="outline" onClick={() => setTransferencia("cambiar")}>
                <Repeat aria-hidden="true" />
                Comprar o vender dólares
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setArqueo(null);
                  setAbrirArqueo(true);
                }}
              >
                <ClipboardCheck aria-hidden="true" />
                Cerrar la caja
              </Button>
              <Button type="button" onClick={() => setAbrirMovimiento(true)}>
                <Plus aria-hidden="true" />
                Movimiento
              </Button>
            </div>
          </>
        }
      />

      <Segmented
        etiqueta="Qué ver de la caja"
        className="w-full md:hidden [&>*]:flex-1"
        tamano="md"
        opciones={[
          { valor: "saldos", etiqueta: "Saldos" },
          { valor: "movimientos", etiqueta: "Movimientos" },
        ]}
        valor={vista}
        onChange={elegirVista}
      />

      {cuantosRevisar > 0 ? (
        <Aviso
          tono="warning"
          compacto
          accion={
            <Button type="button" size="sm" variant="outline" onClick={() => setAbrirRevisar(true)}>
              Revisar
            </Button>
          }
        >
          {cuantosRevisar === 1
            ? "Hay 1 movimiento que cargó otra persona para revisar."
            : `Hay ${cuantosRevisar} movimientos que cargó otra persona para revisar.`}
        </Aviso>
      ) : null}

      <div className={cn(vista === "movimientos" && "max-md:hidden")}>
        <FranjaSaldos
          cajas={cajas ?? []}
          totales={totales ?? null}
          porAcreditar={tarjetas?.totales}
          enCartera={cheques ? chequesEnCartera(cheques.filas) : null}
        />
      </div>

      <Movimientos
        ocultoEnCelular={vista === "saldos"}
        movimientos={movimientos?.filas ?? []}
        cuentas={listaCuentas}
        filtros={filtros}
        onFiltros={setFiltros}
        totales={movimientos?.totales}
        cargando={isFetching}
        socios={socios?.filas ?? []}
        onAnular={setAAnular}
        pie={
          <PieDeLista
            visibles={movimientos?.filas.length ?? 0}
            total={movimientos?.total ?? 0}
            sustantivo="movimientos"
            onVerMas={verMas}
            cargando={isFetching}
          />
        }
      />

      <DialogArqueo
        abierto={abrirArqueo}
        onAbiertoChange={setAbrirArqueo}
        cuentas={listaCuentas}
        guardando={cerrar.isPending}
        resultado={arqueo}
        onCerrar={() => {
          setAbrirArqueo(false);
          setArqueo(null);
        }}
        onConfirmar={(datos) =>
          cerrar.mutate(datos, {
            // No se cierra el dialogo: se cambia por la pantalla del resultado,
            // que es donde recien se ve cuanto habia que tener.
            onSuccess: (r) => setArqueo(r.arqueos),
            onError: mostrarError,
          })
        }
      />

      <DialogRevisar
        abierto={abrirRevisar}
        onCerrar={() => setAbrirRevisar(false)}
        movimientos={paraRevisar?.filas ?? []}
        cuentas={listaCuentas}
        socios={socios?.filas ?? []}
        proveedores={proveedores?.filas ?? []}
        guardando={revisar.isPending}
        onRevisar={(r) =>
          revisar.mutate(r, {
            onSuccess: () =>
              mostrarExito(
                r.concepto ? "Categoría cambiada" : "Revisado",
                "Quedó escrito por qué.",
              ),
            onError: mostrarError,
          })
        }
      />

      <DialogAnularMovimiento
        movimiento={aAnular}
        cuentas={listaCuentas}
        guardando={anular.isPending}
        onCancelar={() => setAAnular(null)}
        onConfirmar={(motivo) => {
          if (!aAnular) return;
          anular.mutate(
            { id: aAnular.id, ...(motivo ? { motivo } : {}) },
            {
              onSuccess: () => {
                setAAnular(null);
                mostrarExito("Movimiento anulado", "La caja y Ganancias vuelven a como estaban.");
              },
              onError: mostrarError,
            },
          );
        }}
      />

      <DialogMovimiento
        abierto={abrirMovimiento}
        onAbiertoChange={setAbrirMovimiento}
        cuentas={listaCuentas}
        saldos={cajas ?? []}
        proveedores={proveedores?.filas ?? []}
        frecuentes={frecuentes?.filas ?? []}
        guardando={registrar.isPending}
        onConfirmar={(datos) =>
          registrar.mutate(datos, {
            onSuccess: () => {
              setAbrirMovimiento(false);
              const cuenta = listaCuentas.find((c) => c.id === datos.cuenta_id);
              mostrarExito(
                `${datos.tipo === "INGRESO" ? "Entró" : "Salió"} ${formatear(datos.importe, cuenta?.moneda ?? "ARS")}`,
                cuenta ? `${cuenta.nombre} · ${cuenta.moneda}` : undefined,
              );
            },
            onError: mostrarError,
          })
        }
      />

      <DialogTransferencia
        modo={transferencia === "cerrado" ? "mover" : transferencia}
        abierto={transferencia !== "cerrado"}
        onAbiertoChange={(a) => !a && setTransferencia("cerrado")}
        cuentas={listaCuentas}
        saldos={cajas ?? []}
        cotizacionSistema={cotizacionSistema}
        guardando={transferir.isPending}
        onConfirmar={(datos) =>
          transferir.mutate(datos, {
            onSuccess: () => {
              setTransferencia("cerrado");
              const origen = listaCuentas.find((c) => c.id === datos.origen_id);
              const destino = listaCuentas.find((c) => c.id === datos.destino_id);
              // Con la moneda: las dos cajas de efectivo se llaman igual, y
              // «De Efectivo a Efectivo» no decia que se compraron dolares.
              const nombre = (c: CuentaFinanciera | undefined, alt: string): string =>
                c ? `${c.nombre} ${c.moneda}` : alt;
              mostrarExito(
                `De ${nombre(origen, "origen")} a ${nombre(destino, "destino")}`,
                datos.cotizacion
                  ? `Cambio a US$ 1 = $ ${textoCotizacion(datos.cotizacion)}`
                  : `${formatear(datos.importe, origen?.moneda ?? "ARS")} movidos`,
              );
            },
            onError: mostrarError,
          })
        }
      />
    </Pagina>
  );
}
